import { useCallback, useEffect, useState, createContext, useContext } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Users, Download, FileText, Send, CheckSquare, ScrollText, Settings, LogOut, Loader2, ShieldCheck, Menu } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../ui/input";
import { OtpVerify } from "../site/OtpVerify";

const AdminCtx = createContext(null);
export const useAdmin = () => useContext(AdminCtx);
export const can = (admin, p) => admin?.permissions?.includes(p);

const NAV = [
  ["/admin", "Overview", LayoutDashboard, "view_analytics"], ["/admin/waitlist", "Waitlist", Users, "view_analytics"],
  ["/admin/exports", "Export Center", Download, "export"], ["/admin/email-templates", "Email Templates", FileText, "view_analytics"],
  ["/admin/campaigns", "Campaigns", Send, "view_analytics"], ["/admin/approvals", "Approvals", CheckSquare, "view_analytics"],
  ["/admin/delivery-logs", "Delivery & Suppression", ScrollText, "view_analytics"], ["/admin/settings", "Settings", Settings, "view_analytics"],
];

const AdminLogin = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [mfa, setMfa] = useState(null);
  const [totp, setTotp] = useState("");
  const [loading, setLoading] = useState(false);
  const send = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const { data } = await api.post("/admin/auth/request-otp", { email: email.trim() }); setChallenge({ ...data, email: email.trim().toLowerCase() }); toast.success(data.message); }
    catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  const verified = (data, creds) => (data.mfa_required ? setMfa(creds) : onDone());
  const submitTotp = async () => {
    try { await api.post("/admin/auth/verify-otp", { ...mfa, email: challenge.email, totp }); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-5 relative bg-[#080A08]" data-testid="admin-login">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="relative w-full max-w-md card-surface lime-glow p-8">
        <img src="/logo.png" alt="CricAnalyze" className="h-12 bg-white rounded-lg px-2 py-1" />
        <h1 className="mt-6 text-3xl font-black uppercase flex items-center gap-2"><ShieldCheck className="text-[#B7FF3C]" />Admin Access</h1>
        <p className="mt-1 mb-6 text-sm text-[#A3ADA3]">Authorized Buddy Innovations &amp; Technologies administrators only. All access is audit-logged.</p>
        {mfa ? (
          <div className="space-y-4">
            <p className="text-sm text-[#A3ADA3]">Enter the 6-digit code from your authenticator app.</p>
            <Input value={totp} onChange={(e) => setTotp(e.target.value)} maxLength={6} data-testid="admin-totp-input" className="h-12 bg-[#080A08] border-[#2A362A] font-mono tracking-widest" />
            <button onClick={submitTotp} data-testid="admin-totp-submit" className="w-full py-3.5 rounded-full bg-[#B7FF3C] text-[#080A08] font-bold">Verify &amp; Sign In</button>
          </div>
        ) : challenge ? <OtpVerify challenge={challenge} onVerified={verified} verifyPath="/admin/auth/verify-otp" resendPath="/admin/auth/request-otp" onChallenge={(cid) => setChallenge({ ...challenge, challenge_id: cid })} /> : (
          <form onSubmit={send} className="space-y-4">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin email" data-testid="admin-email-input" className="h-12 bg-[#080A08] border-[#2A362A]" />
            <button disabled={loading} data-testid="admin-send-otp-btn" className="w-full py-3.5 rounded-full bg-[#B7FF3C] text-[#080A08] font-bold flex justify-center">{loading ? <Loader2 className="animate-spin" size={18} /> : "Send Login Code"}</button>
          </form>
        )}
      </div>
    </div>
  );
};

export default function AdminLayout() {
  const [admin, setAdmin] = useState(null);
  const [state, setState] = useState("loading");
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { pathname } = useLocation();
  const load = useCallback(() => api.get("/admin/auth/me").then((r) => { setAdmin(r.data); setState("ready"); }).catch(() => setState("auth")), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = api.interceptors.response.use((r) => r, (e) => { if (e.response?.status === 401 && e.config.url.startsWith("/admin")) setState("auth"); return Promise.reject(e); });
    return () => api.interceptors.response.eject(id);
  }, []);
  const logout = async () => { await api.post("/admin/auth/logout"); setState("auth"); nav("/admin"); };

  if (state === "loading") return <div className="min-h-screen flex items-center justify-center bg-[#080A08]"><Loader2 className="animate-spin text-[#B7FF3C]" /></div>;
  if (state === "auth") return <AdminLogin onDone={load} />;

  return (
    <AdminCtx.Provider value={admin}>
      <div className="min-h-screen bg-[#080A08] flex" data-testid="admin-layout">
        <aside className={`fixed lg:sticky top-0 z-40 h-screen w-64 shrink-0 border-r border-[#1E281E] bg-[#0B0E0B] flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
          <div className="p-5 border-b border-[#1E281E]"><img src="/logo.png" alt="CricAnalyze" className="h-10 bg-white rounded-lg px-2 py-1" /><p className="mt-2 eyebrow !text-[10px]">Admin Console</p></div>
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {NAV.filter((n) => can(admin, n[3])).map(([to, label, I]) => (
              <NavLink key={to} to={to} end={to === "/admin"} onClick={() => setOpen(false)} data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-[#B7FF3C]/10 text-[#B7FF3C] border border-[#B7FF3C]/20" : "text-[#A3ADA3] hover:text-white hover:bg-[#111511]"}`}>
                <I size={17} />{label}
              </NavLink>
            ))}
          </nav>
          <div className="p-4 border-t border-[#1E281E] text-xs">
            <p className="text-white truncate" data-testid="admin-current-email">{admin.email}</p>
            <p className="text-[#7C8A7C] font-mono uppercase mt-0.5" data-testid="admin-current-role">{admin.role.replace(/_/g, " ")}</p>
            <button onClick={logout} data-testid="admin-logout-btn" className="mt-3 flex items-center gap-2 text-[#A3ADA3] hover:text-white"><LogOut size={14} />Sign out</button>
          </div>
        </aside>
        <div className="flex-1 min-w-0">
          <div className="lg:hidden flex items-center justify-between px-4 h-14 border-b border-[#1E281E]"><button onClick={() => setOpen(!open)} data-testid="admin-mobile-menu"><Menu /></button><span className="eyebrow">CricAnalyze Admin</span></div>
          <main className="p-5 lg:p-8 max-w-[1500px]">{(() => {
            const item = NAV.find((n) => n[0] === pathname.replace(/\/$/, ""));
            return item && !can(admin, item[3]) ? <div className="card-surface p-10 text-center" data-testid="admin-no-permission"><p className="font-display text-2xl uppercase">No permission</p><p className="text-sm text-[#7C8A7C] mt-2">Your role doesn't have access to this section.</p></div> : <Outlet />;
          })()}</main>
        </div>
      </div>
    </AdminCtx.Provider>
  );
}
